import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Connect from 'components/organisms/connect/connect';
import { useAppDispatch, useAppSelector } from 'store';
import { useMultiPatientCall } from 'infrastructure/providers/multi-patient-call-context';
import { useConnectCall } from 'infrastructure/hooks/use-connect-call';
import { useCallStartedEventListener } from 'infrastructure/customEvents/callEvents';
import {
  useGetCcmInteractionTime,
  useGetRpmInteractionTime,
} from 'components/organisms/interactiveTime/hooks';
import { convertSecondsToTime } from 'utils/timeHelper';
import {
  addTransferConnection,
  cancelTransfer,
  completeTransfer,
  endActiveContact,
  getCallControlSnapshot,
  hangUpActiveContact,
  hasConnectedVoiceContact,
  holdContact,
  joinTransferConnections,
  resumeContact,
  sendDigits,
  setMuted,
  swapTransferConnections,
} from 'services/connectService';
import { showResult } from 'infrastructure/hooks/utils';

import DraggableModal from '../draggableModal';
import CallPanel from './call-panel';
import s from './styles.module.scss';

/**
 * The modal shell is unchanged: DraggableModal, handleClose and the
 * linkedCallsReadings dispatch. What changed is the body — CallPanel replaces
 * the .connect-wrapper + .actions-wrapper pair, and the Amazon CCP iframe is
 * mounted hidden underneath it because softphone audio lives in there.
 */
const ConnectModal = () => {
  const [_callId, setCallId] = useState<string>('');
  const { startCall, endCall } = useMultiPatientCall();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isOpen = useAppSelector((state) => state.connect.connectPopoutOpen);
  const connectDetails = useAppSelector(
    (state) => state.connect.connectDetails,
  );
  const {
    connect: { handleConnectPopoutOpen, connectDetails: handleConnectDetails },
    linkedCallsReadings: { setIsPopupOpen, resetState },
  } = useAppDispatch();

  const { t } = useTranslation();
  const { callState, markHeld, markMuted, reset } = useConnectCall();

  const patientGuid = connectDetails.guid;
  const isRpmCall = !!connectDetails.isRpmCall;
  const isEnded = callState.phase === 'ended';

  // Only asked for on the wrap-up board, which is the only place it is shown.
  const { rpmInteractionTime, isFetching: isFetchingRpm } =
    useGetRpmInteractionTime({
      patientGuid,
      enabled: isEnded && !!patientGuid && isRpmCall,
    });
  const { ccmInteractionTime, isFetching: isFetchingCcm } =
    useGetCcmInteractionTime({
      patientGuid,
      enabled: isEnded && !!patientGuid && !isRpmCall,
    });

  const interactionTime = isRpmCall ? rpmInteractionTime : ccmInteractionTime;
  const isFetchingInteractionTime = isRpmCall ? isFetchingRpm : isFetchingCcm;
  const interactiveTotal = isFetchingInteractionTime
    ? '--:--:--'
    : convertSecondsToTime(interactionTime?.totalTime);

  const handleClose = () => {
    // A live call must be ended from the panel before the window can close.
    // Closing flips isOpen, which terminates the CCP and removes the softphone
    // iframe — the audio dies with it while the backend still shows the call
    // open, because the contact handlers are unsubscribed before ENDED arrives.
    // Both conditions, deliberately. The phase alone is not enough authority to
    // refuse a close: any tab that reaches 'connected' and then misses its
    // ENDED event would keep the guard armed forever, and with headerless
    // chrome and closeOnEscape={false} the agent would have no way out of the
    // window at all. hasConnectedVoiceContact() asks the SDK whether there is
    // really a live call in this tab to protect.
    if (callState.phase === 'connected' && hasConnectedVoiceContact()) {
      showResult(t('connectPanel.closeWhileOnCall'));
      return;
    }
    // Cancels a contact that is still ringing. A contact that has already
    // ended is left alone: closing this window must not close the contact, or
    // the agent loses the after-call work they are still in. Gated on this
    // tab's own phase: the shared worker syncs contacts to every open popup,
    // and only the tab that owns the call ever leaves 'idle' — an
    // unconditional sweep here would let closing an idle popup in one tab kill
    // a ring that another tab is running.
    if (callState.phase !== 'idle') endActiveContact();
    reset();
    setIsCollapsed(false);

    const details = {
      phone: '',
      guid: '',
      isRpmCall: true,
      readingId: '',
      deviceId: '',
      actionFrom: undefined,
    };
    handleConnectDetails(details);
    handleConnectPopoutOpen(false);
    setIsPopupOpen(false);
    resetState();
  };

  // Hold and mute flip optimistically and roll back with a toast if Connect
  // rejects the call — a tile must never keep claiming a hold or a mute that
  // did not actually happen (that is a live mic pointed at a patient).
  const handleHold = () => {
    const next = !callState.isHeld;
    markHeld(next);
    const onFailure = () => {
      markHeld(!next);
      showResult(
        t(next ? 'connectPanel.holdFailed' : 'connectPanel.resumeFailed'),
      );
    };
    if (next) holdContact({ onFailure });
    else resumeContact({ onFailure });
  };

  // Read by event listeners that outlive the render they were created in.
  const phaseRef = useRef(callState.phase);
  phaseRef.current = callState.phase;
  const isMutedRef = useRef(callState.isMuted);
  isMutedRef.current = callState.isMuted;

  const applyMuted = useCallback(
    (next: boolean, opts?: { armIfRinging?: boolean }) => {
      // Mute is server-side (muteParticipant) and Connect rejects it while the
      // contact is still CONNECTING. That rejection is not a failure the agent
      // needs to hear about: nothing they say is audible to the patient before
      // the answer, so the tile stays engaged as an ARMED mute and the
      // callStarted listener below applies it the moment the patient picks up.
      const armed =
        opts?.armIfRinging === true && phaseRef.current === 'connecting';
      markMuted(next);
      setMuted(next, {
        onFailure: () => {
          if (armed) return;
          markMuted(!next);
          showResult(
            t(next ? 'connectPanel.muteFailed' : 'connectPanel.unmuteFailed'),
          );
        },
      });
    },
    [markMuted, t],
  );

  const handleMute = () => {
    applyMuted(!callState.isMuted, { armIfRinging: true });
  };

  // The moment the patient answers, make the SDK agree with the tile. Covers a
  // mute armed during the ring (rejected above, so the server still has the mic
  // open) and the inverse — a ring-time mute that DID land server-side and was
  // then toggled off. Failures here are real: the patient is on the line now,
  // so applyMuted's default rollback-and-toast path is the right one.
  useCallStartedEventListener(
    useCallback(() => {
      const desired = isMutedRef.current;
      const serverMuted = getCallControlSnapshot()?.isAgentMuted ?? false;
      if (desired !== serverMuted) applyMuted(desired);
    }, [applyMuted]),
  );

  const handleDigit = (digit: string) =>
    sendDigits(digit, {
      onFailure: () => showResult(t('connectPanel.digitFailed')),
    });

  const handleEndCall = () =>
    hangUpActiveContact({
      onFailure: () => showResult(t('connectPanel.endFailed')),
    });

  const handleTransferDial = (target: {
    endpointId?: string;
    rawNumber?: string;
  }) => {
    // A transfer that silently fails to dial is worse than one that says so —
    // the agent would sit waiting for a party that was never called.
    addTransferConnection(target, {
      onFailure: () => showResult(t('connectPanel.transfer.failed')),
    });
  };

  return (
    <DraggableModal
      isOpen={isOpen}
      toggle={handleClose}
      // The panel draws its own header, so the modal's chrome is suppressed —
      // otherwise two stacked headers ship.
      headerless
      // Escape is document-level in DraggableModal, so with it enabled a key
      // aimed at any dropdown on the page would cancel an in-flight ring. The
      // call window closes only through its own explicit controls.
      closeOnEscape={false}
      id="connect-modal"
      footer={false}
      modalClassName={s.modal}
      bodyClassName={s['modal-body']}
      dataCy="connect-modal"
      destroyOnClose={false}
    >
      <CallPanel
        isOpen={isOpen}
        callState={callState}
        patientName={connectDetails.patientName}
        phoneNumber={connectDetails.phone}
        isRpmCall={isRpmCall}
        patientGuid={patientGuid}
        interactiveTotal={interactiveTotal}
        isCollapsed={isCollapsed}
        onHold={handleHold}
        onMute={handleMute}
        onDigit={handleDigit}
        onTransferDial={handleTransferDial}
        onTransferSwap={() =>
          swapTransferConnections({
            onFailure: () => showResult(t('connectPanel.transfer.swapFailed')),
          })
        }
        onTransferJoin={() =>
          joinTransferConnections({
            onFailure: () => showResult(t('connectPanel.transfer.joinFailed')),
          })
        }
        onTransferComplete={() =>
          completeTransfer({
            onFailure: () =>
              showResult(t('connectPanel.transfer.handoffFailed')),
          })
        }
        onTransferCancel={() =>
          cancelTransfer({
            onFailure: () =>
              showResult(t('connectPanel.transfer.cancelFailed')),
          })
        }
        onEndCall={handleEndCall}
        onLinkReadings={() => setIsPopupOpen(true)}
        onMinimize={() => setIsCollapsed(true)}
        onClose={handleClose}
        onToggleCollapse={() => setIsCollapsed((prev) => !prev)}
      />

      {/* Softphone audio lives in here. Hidden, never unmounted. */}
      <Connect
        isOpen={isOpen}
        phoneNumber={connectDetails.phone}
        patientGuid={patientGuid}
        setCallId={setCallId}
        isRpmCall={isRpmCall}
        readingId={connectDetails.readingId}
        deviceId={connectDetails.deviceId}
        actionFrom={connectDetails.actionFrom}
        onCallStarted={startCall}
        onCallEnded={endCall}
      />
    </DraggableModal>
  );
};

export default ConnectModal;
